import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Star, Shield, Truck, Headphones, Zap, ChevronRight } from 'lucide-react';
import { productsAPI } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';

const CATEGORIES = [
  { name: 'Laptops', icon: '💻', color: '#7c6dfa' }, { name: 'Smartphones', icon: '📱', color: '#8b5cf6' },
  { name: 'Audio', icon: '🎧', color: '#06b6d4' }, { name: 'Tablets', icon: '📟', color: '#10b981' },
  { name: 'Cameras', icon: '📷', color: '#f59e0b' }, { name: 'Accessories', icon: '🖱️', color: '#ef4444' },
];

const TESTIMONIALS = [
  { name: 'Ahmed Ben Ali', role: 'Software Engineer', rating: 5, text: 'Best electronics store in Tunisia! Fast delivery and authentic products. My MacBook Pro arrived in perfect condition.' },
  { name: 'Sarah Johnson', role: 'UX Designer', rating: 5, text: 'Amazing customer service and the product quality is outstanding. The Sony headphones are exactly as described.' },
  { name: 'Mohamed Trabelsi', role: 'IT Manager', rating: 4, text: 'Professional store with competitive prices. I ordered 5 laptops for our company and everything was perfect.' },
];

function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { t } = useSettings();
  return (
    <div className="product-card" onClick={() => navigate(`/products/${product._id}`)}>
      {product.discount > 0 && <div style={{ position:'absolute', top:12, left:12, zIndex:2 }}><span className="badge badge-discount">-{product.discount}%</span></div>}
      {product.isFeatured && <div style={{ position:'absolute', top:12, right:12, zIndex:2 }}><span className="badge badge-featured">⭐ Featured</span></div>}
      <div className="product-card-img-wrap">
        <img src={product.thumbnail || 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400'} alt={product.name} className="product-card-image" />
        <div className="product-card-overlay">
          <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); addItem(product); }}>{t('products','add_to_cart')}</button>
          <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate(`/products/${product._id}`); }}>View</button>
        </div>
      </div>
      <div className="product-card-body">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <span className="product-card-brand">{product.brand}</span>
          <span style={{ fontSize:11, color:'var(--text-muted)', background:'var(--bg-secondary)', padding:'2px 8px', borderRadius:6 }}>{product.category}</span>
        </div>
        <h3 className="product-card-name">{product.name}</h3>
        <div className="product-card-rating">
          {[...Array(5)].map((_,i) => <span key={i} style={{ color: i < Math.round(product.rating||0) ? 'var(--gold)' : 'var(--border-light)' }}>★</span>)}
          <span>({product.numReviews||0})</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <span className="product-card-price">${product.price?.toLocaleString()}</span>
            {product.originalPrice > product.price && <span className="product-card-original">${product.originalPrice?.toLocaleString()}</span>}
          </div>
          <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); addItem(product); }}>{t('products','add')}</button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { t } = useSettings();

  useEffect(() => {
    productsAPI.getAll({ featured:true, limit:8 }).then(r => setFeatured(r.data.products)).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section style={{ minHeight: '88vh', display:'flex', alignItems:'center', position:'relative', overflow:'hidden', background:'var(--bg-primary)' }}>
        <div style={{ position:'absolute', inset:0, background:'var(--gradient-hero)', pointerEvents:'none' }} />
        {/* Decorative orbs */}
        <div style={{ position:'absolute', top:'15%', right:'8%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle, rgba(124,109,250,0.12) 0%, transparent 70%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:'10%', left:'5%', width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle, rgba(232,160,32,0.08) 0%, transparent 70%)', pointerEvents:'none' }} />
        <div className="container" style={{ position:'relative', paddingTop:80, paddingBottom:80 }}>
          <div className="hero-content" style={{ maxWidth:700 }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'var(--accent-dim)', border:'1px solid rgba(124,109,250,0.25)', borderRadius:30, padding:'7px 18px', marginBottom:28 }}>
              <Zap size={14} color="var(--accent)" fill="var(--accent)" />
              <span style={{ fontSize:13, color:'var(--accent)', fontWeight:700 }}>{t('home','hero_badge')}</span>
            </div>
            <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(44px,7vw,88px)', fontWeight:700, lineHeight:1.02, marginBottom:28, letterSpacing:'-0.04em' }}>
              {t('home','hero_title1')}<br />
              <span style={{ background:'linear-gradient(135deg, var(--accent) 0%, var(--gold) 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
                {t('home','hero_title2')}
              </span><br />
              {t('home','hero_title3')}
            </h1>
            <p style={{ fontSize:18, color:'var(--text-secondary)', lineHeight:1.75, marginBottom:40, maxWidth:540 }}>{t('home','hero_sub')}</p>
            <div className="hero-btns" style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <button className="btn btn-primary btn-xl" onClick={() => navigate('/products')}>
                {t('home','shop_now')} <ArrowRight size={20} />
              </button>
              <button className="btn btn-secondary btn-xl" onClick={() => navigate('/products?featured=true')}>
                {t('home','view_featured')}
              </button>
            </div>
            {/* Trust badges */}
            <div className="hero-stats" style={{ display:'flex', gap:28, marginTop:52, flexWrap:'wrap' }}>
              {[['50K+','Happy Customers'],['10K+','Products'],['99%','Satisfaction'],['24/7','Support']].map(([num,label]) => (
                <div key={label}>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:26, fontWeight:800, color:'var(--accent)' }}>{num}</div>
                  <div style={{ color:'var(--text-muted)', fontSize:12, marginTop:2 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES STRIP ── */}
      <section className="features-strip" style={{ padding:'0', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px,1fr))', gap:0 }}>
            {[
              [Truck, t('home','free_shipping'), t('home','free_shipping_sub'), '#7c6dfa'],
              [Shield, t('home','warranty'), t('home','warranty_sub'), '#22c55e'],
              [Headphones, t('home','support'), t('home','support_sub'), '#f59e0b'],
              [Zap, t('home','fast_delivery'), t('home','fast_delivery_sub'), '#38bdf8'],
            ].map(([Icon, title, desc, color], i) => (
              <div key={title} style={{ display:'flex', alignItems:'center', gap:16, padding:'28px 32px', borderInlineEnd: i < 3 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width:48, height:48, background:`${color}18`, borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon size={22} color={color} />
                </div>
                <div>
                  <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:15 }}>{title}</div>
                  <div style={{ color:'var(--text-muted)', fontSize:13, marginTop:2 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIES ── */}
      <section style={{ padding:'96px 0 64px' }}>
        <div className="container">
          <div style={{ marginBottom:48 }}>
            <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--accent)', marginBottom:12 }}>Browse</div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(28px,4vw,44px)', fontWeight:700, letterSpacing:'-0.03em' }}>{t('home','categories')}</h2>
            <p style={{ color:'var(--text-secondary)', marginTop:8, fontSize:16 }}>{t('home','categories_sub')}</p>
          </div>
          <div className="categories-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(170px,1fr))', gap:14 }}>
            {CATEGORIES.map(cat => (
              <Link key={cat.name} to={`/products?category=${cat.name}`}
                style={{ background:'var(--bg-card)', border:'1.5px solid var(--border)', borderRadius:20, padding:'32px 16px', textAlign:'center', transition:'var(--transition)', display:'block' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = `0 16px 40px ${cat.color}20`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ fontSize:40, marginBottom:14 }}>{cat.icon}</div>
                <div style={{ fontFamily:'var(--font-display)', fontWeight:700, fontSize:15 }}>{cat.name}</div>
                <div style={{ marginTop:8, display:'flex', alignItems:'center', justifyContent:'center', gap:4, color:cat.color, fontSize:12, fontWeight:600 }}>
                  Shop <ChevronRight size={13} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED PRODUCTS ── */}
      <section style={{ padding:'0 0 96px' }}>
        <div className="container">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:40, flexWrap:'wrap', gap:16 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--accent)', marginBottom:12 }}>Handpicked</div>
              <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(28px,4vw,44px)', fontWeight:700, letterSpacing:'-0.03em' }}>{t('home','featured')}</h2>
              <p style={{ color:'var(--text-secondary)', marginTop:8 }}>{t('home','featured_sub')}</p>
            </div>
            <Link to="/products" className="btn btn-secondary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              {t('home','view_all')} <ArrowRight size={15} />
            </Link>
          </div>
          {loading ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:24 }}>
              {[...Array(4)].map((_,i) => <div key={i} className="skeleton" style={{ height:380, borderRadius:20 }} />)}
            </div>
          ) : (
            <div className="products-grid">
              {featured.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section style={{ padding:'96px 0', background:'var(--bg-secondary)', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)' }}>
        <div className="container">
          <div style={{ textAlign:'center', marginBottom:56 }}>
            <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:'var(--accent)', marginBottom:12 }}>Reviews</div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(28px,4vw,44px)', fontWeight:700, letterSpacing:'-0.03em' }}>{t('home','testimonials')}</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px,1fr))', gap:20 }}>
            {TESTIMONIALS.map((t_, i) => (
              <div key={i} className="card" style={{ position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg, var(--accent), var(--gold))' }} />
                <div style={{ display:'flex', gap:2, marginBottom:16 }}>
                  {[...Array(t_.rating)].map((_,j) => <span key={j} style={{ color:'var(--gold)', fontSize:18 }}>★</span>)}
                </div>
                <p style={{ color:'var(--text-secondary)', lineHeight:1.7, fontSize:15, marginBottom:20, fontStyle:'italic' }}>"{t_.text}"</p>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:42, height:42, borderRadius:'50%', background:'linear-gradient(135deg, var(--accent), var(--gold))', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:16, color:'white' }}>{t_.name[0]}</div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14 }}>{t_.name}</div>
                    <div style={{ color:'var(--text-muted)', fontSize:12 }}>{t_.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <section style={{ padding:'96px 0' }}>
        <div className="container">
          <div style={{ maxWidth:600, margin:'0 auto', textAlign:'center' }}>
            <div style={{ width:64, height:64, background:'linear-gradient(135deg, var(--accent), var(--gold))', borderRadius:20, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px' }}>
              <Zap size={28} color="white" fill="white" />
            </div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(28px,4vw,40px)', fontWeight:700, marginBottom:12, letterSpacing:'-0.03em' }}>{t('home','newsletter_title')}</h2>
            <p style={{ color:'var(--text-secondary)', marginBottom:32, fontSize:16 }}>{t('home','newsletter_sub')}</p>
            <form onSubmit={e => { e.preventDefault(); setEmail(''); }} className="newsletter-form" style={{ display:'flex', gap:10, maxWidth:460, margin:'0 auto' }}>
              <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('home','newsletter_placeholder')} required style={{ flex:1 }} />
              <button type="submit" className="btn btn-primary">{t('home','newsletter_btn')}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
